import { useEffect, useMemo, useState } from 'react'
import {
  legacyScheduleBatchHistory,
  legacyScheduleBatchOfficeOptions,
  type LegacyScheduleBatchHistory,
} from '../../mock/legacyScheduleBatch'
import './LegacyScheduleBatchList.css'

type DeleteModal = 'other-warning' | 'delete-confirm' | 'success' | null

const weekdayNames = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일']

function todayLocal() {
  const today = new Date()
  const year = String(today.getFullYear()).padStart(4, '0')
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function parseLocalDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(year, month - 1, day, 12)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null
  return date
}

function withWeekday(value: string) {
  const date = parseLocalDate(value)
  return date ? `${value}(${weekdayNames[date.getDay()]})` : value
}

function normalizeTimeRange(value: string) {
  const [start = '', end = ''] = value.split('~').map((part) => part.trim())
  return end ? `${start} ~ ${end}` : start
}

function filterHistory(items: LegacyScheduleBatchHistory[], officeId: string, startDate: string, endDate: string) {
  const officeName = legacyScheduleBatchOfficeOptions.find((item) => item.id === officeId)?.name ?? ''
  return items.filter((item) => {
    const registeredDate = item.registeredAt.slice(0, 10)
    return item.office === officeName && registeredDate >= startDate && registeredDate <= endDate
  })
}

export function LegacyScheduleBatchList() {
  const initial = useMemo(() => {
    const params = new URLSearchParams(window.location.search)
    const requestedOffice = params.get('office_id')
    const officeId = legacyScheduleBatchOfficeOptions.some((item) => item.id === requestedOffice)
      ? requestedOffice as string
      : legacyScheduleBatchOfficeOptions[0]?.id ?? ''
    const today = todayLocal()
    const startDate = params.get('start_day') || today
    const endDate = params.get('end_day') || today
    const hasQuery = ['office_id', 'start_day', 'end_day', 'detail_id'].some((key) => params.has(key))
    const initialItems = hasQuery ? filterHistory(legacyScheduleBatchHistory, officeId, startDate, endDate) : []
    const requestedDetail = params.get('detail_id')
    const selectedId = initialItems.some((item) => item.id === requestedDetail)
      ? requestedDetail
      : initialItems[0]?.id ?? null
    return { officeId, startDate, endDate, hasQuery, selectedId }
  }, [])

  const [officeId, setOfficeId] = useState(initial.officeId)
  const [officeOpen, setOfficeOpen] = useState(false)
  const [officeSearch, setOfficeSearch] = useState('')
  const [startDate, setStartDate] = useState(initial.startDate)
  const [endDate, setEndDate] = useState(initial.endDate)
  const [items, setItems] = useState(legacyScheduleBatchHistory)
  const [selectedId, setSelectedId] = useState<string | null>(initial.selectedId)
  const [deleteModal, setDeleteModal] = useState<DeleteModal>(null)

  useEffect(() => {
    document.title = '스케줄 일괄 등록 내역'
  }, [])

  useEffect(() => {
    if (!officeOpen) return
    const closeOffice = (event: PointerEvent) => {
      if (!(event.target as Element | null)?.closest('#office_select_chosen')) setOfficeOpen(false)
    }
    document.addEventListener('pointerdown', closeOffice)
    return () => document.removeEventListener('pointerdown', closeOffice)
  }, [officeOpen])

  const visibleItems = useMemo(
    () => initial.hasQuery ? filterHistory(items, initial.officeId, initial.startDate, initial.endDate) : [],
    [initial, items],
  )
  const selected = visibleItems.find((item) => item.id === selectedId)
  const selectedOffice = legacyScheduleBatchOfficeOptions.find((item) => item.id === officeId) ?? legacyScheduleBatchOfficeOptions[0]
  const officeResults = legacyScheduleBatchOfficeOptions.filter((item) => item.name.includes(officeSearch.trim()))

  const makeUrl = (detailId?: string) => {
    const params = new URLSearchParams({
      office_id: officeId,
      start_day: startDate,
      end_day: endDate,
    })
    if (detailId) params.set('detail_id', detailId)
    return `${window.location.pathname}?${params.toString()}`
  }

  const search = (form: HTMLFormElement) => {
    const startField = form.elements.namedItem('start_day') as HTMLInputElement
    const endField = form.elements.namedItem('end_day') as HTMLInputElement
    startField.setCustomValidity('')
    endField.setCustomValidity('')
    if (!form.checkValidity()) {
      form.reportValidity()
      return
    }
    if (!parseLocalDate(startDate)) {
      startField.setCustomValidity('유효한 날짜를 입력하세요.')
      startField.reportValidity()
      return
    }
    if (!parseLocalDate(endDate)) {
      endField.setCustomValidity('유효한 날짜를 입력하세요.')
      endField.reportValidity()
      return
    }
    if (startDate > endDate) {
      startField.setCustomValidity('종료일자가 시작일자 보다 앞설 수 없습니다.')
      startField.reportValidity()
      return
    }
    window.location.assign(makeUrl())
  }

  const openDetail = (item: LegacyScheduleBatchHistory) => {
    if (item.id === selectedId) return
    window.location.assign(makeUrl(item.id))
  }

  const beginDelete = () => {
    if (!selected) return
    setDeleteModal(selected.isOwn ? 'delete-confirm' : 'other-warning')
  }

  const deleteSelected = () => {
    if (!selected) return
    const nextItems = items.filter((item) => item.id !== selected.id)
    const nextVisibleItems = filterHistory(nextItems, initial.officeId, initial.startDate, initial.endDate)
    const nextId = nextVisibleItems[0]?.id ?? null
    setItems(nextItems)
    setSelectedId(nextId)
    const params = new URLSearchParams({
      office_id: initial.officeId,
      start_day: initial.startDate,
      end_day: initial.endDate,
    })
    if (nextId) params.set('detail_id', nextId)
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`)
    setDeleteModal('success')
  }

  return <div className="legacy-s05 l_main_content_wrap">
    <h1 className="g_title">스케줄 일괄 등록 내역</h1>
    <div className="l_content_wrap">
      <section className="g_filter_wrap l_schedule_search_wrap">
        <form id="l_sched_batch_search_form" onSubmit={(event) => { event.preventDefault(); search(event.currentTarget) }}>
          <div className="g_row"><div className="g_input_wrap required"><span className="g_label title">지점</span><select id="office_select" name="office_id" className="g_input chosen-select l_office_select width_2x" required value={officeId} onChange={(event) => setOfficeId(event.target.value)} tabIndex={-1} aria-hidden="true" style={{ display: 'none' }}>{legacyScheduleBatchOfficeOptions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select><div id="office_select_chosen" className={`chosen-container chosen-container-single${officeOpen ? ' chosen-with-drop chosen-container-active' : ''}`}><a className="chosen-single" href="#office_select_chosen" role="button" aria-haspopup="listbox" aria-expanded={officeOpen} onClick={(event) => { event.preventDefault(); setOfficeOpen((open) => !open) }}><span>{selectedOffice?.name ?? ''}</span><div><b /></div></a><div className="chosen-drop"><div className="chosen-search"><input className="chosen-search-input" type="text" aria-label="지점 검색" value={officeSearch} onChange={(event) => setOfficeSearch(event.target.value)} /></div><ul className="chosen-results" role="listbox">{officeResults.map((item) => <li key={item.id} className={`active-result${item.id === officeId ? ' result-selected' : ''}`} role="option" aria-selected={item.id === officeId} onClick={() => { setOfficeId(item.id); setOfficeOpen(false); setOfficeSearch('') }}>{item.name}</li>)}</ul></div></div></div></div>
          <div className="g_row"><div className="g_input_wrap required"><span className="g_label title">일괄 등록 기간</span><div className="l_date_range_wrap"><input id="l_sched_batch_start" className="g_input g_input_datepicker l_date_picker" name="start_day" pattern="^\d{4}-\d{2}-\d{2}$" title="숫자와 - 만 입력 가능합니다." required value={startDate} onChange={(event) => { event.currentTarget.setCustomValidity(''); setStartDate(event.target.value) }} /><span>~</span><input id="l_sched_batch_end" className="g_input g_input_datepicker l_date_picker" name="end_day" pattern="^\d{4}-\d{2}-\d{2}$" title="숫자와 - 만 입력 가능합니다." required value={endDate} onChange={(event) => { event.currentTarget.setCustomValidity(''); setEndDate(event.target.value) }} /></div></div></div>
          <div className="l_search_func_wrap"><button className="g_btn g_btn_submit search" type="submit">검색</button></div>
        </form>
      </section>
      <section className="l_list_container"><div className="l_upload_list_section_wrap">
        <section className="g_card l_card l_list_section"><section className="g_list_wrap l_history_list"><table id="l_hitory_table" className="g_list g_table_excel"><thead><tr><th>등록일</th><th>제목</th><th>스케줄 수</th><th>작업자</th></tr></thead><tbody className={`l_data_body${visibleItems.length ? '' : ' no_data'}`}>{visibleItems.length ? visibleItems.map((item) => <tr className="l_data_row" {...(item.id === selectedId ? { selected: 'selected' } : {})} key={item.id}><td><span id="l_one_queue_created_date" data-row-pk={item.id}>{withWeekday(item.registeredAt.slice(0, 10))}</span></td><td><span id="l_one_queue_schedule_title" className="g_item" role="button" tabIndex={0} data-row-pk={item.id} onClick={() => openDetail(item)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openDetail(item) } }}>{item.title}</span></td><td><span id="l_one_queue_schedule_count" data-row-pk={item.id}>{item.scheduleCount}</span></td><td><span id="l_one_queue_staff_name" data-row-pk={item.id}>{item.staff || '-'}</span></td></tr>) : <tr className="l_no_data_row"><td colSpan={7}>검색 결과가 없습니다.</td></tr>}</tbody></table></section></section>
        <section className="g_card l_card l_row_detail_section"><div className="l_list_detail_content_wrap">
          <section className="l_detail_top"><ReadRow id="l_office" label="지점" value={selected?.office ?? ''} /><ReadRow id="l_one_queue_created_date" label="등록일시" value={selected?.registeredAt ?? ''} wide /><ReadRow id="l_one_queue_schedule_title" label="제목" value={selected?.title ?? ''} wide /><div className="g_row"><div className="g_input_wrap"><span className="g_label title l_title">스케줄 기간</span><div className="l_date_range_wrap"><input id="l_one_queue_schedule_start_date" className="g_input g_input_datepicker l_date_picker" name="start_day" pattern="^\d{4}-\d{2}-\d{2}$" title="숫자와 - 만 입력 가능합니다." readOnly value={selected?.startDate ?? ''} /><span>~</span><input id="l_one_queue_schedule_end_date" className="g_input g_input_datepicker l_date_picker" name="end_day" pattern="^\d{4}-\d{2}-\d{2}$" title="숫자와 - 만 입력 가능합니다." readOnly value={selected?.endDate ?? ''} /></div></div></div></section>
          <section className="g_list_wrap l_hitory_detail"><table id="l_hitory_table" className="g_list g_table_excel"><thead><tr><th>스케줄일</th><th>시간</th><th>작업담당</th></tr></thead><tbody className={`l_data_body${selected?.schedules.length ? '' : ' no_data'}`}>{selected?.schedules.length ? selected.schedules.map((row, index) => { const worker = row.worker === '-' ? '' : row.worker; const [startTime = '', endTime = ''] = normalizeTimeRange(row.time).split(' ~ '); return <tr className="l_data_row" key={`${row.date}-${index}`}><td><span id="l_schedule_date">{withWeekday(row.date)}</span></td><td><span id="l_photo_start">{startTime}</span> ~ <span id="l_photo_end">{endTime}</span></td><td className={`l_workgroup_cell${worker.trim() ? '' : ' no_select'}`}><span id="l_workgroup_name">{worker}</span></td></tr> }) : <tr className="l_no_data_row"><td colSpan={7}>검색 결과가 없습니다.</td></tr>}</tbody></table></section>
          <div className="g_func_wrap l_detail_func_wrap"><button id="l_del_history_btn" className={`g_btn delete l_del_btn${selected ? ' show' : ''}`} type="button" onClick={beginDelete}>삭제</button></div>
        </div></section>
      </div></section>
    </div>
    <DeleteDialog state={deleteModal} onNo={() => setDeleteModal(null)} onYes={() => {
      if (deleteModal === 'other-warning') setDeleteModal('delete-confirm')
      else if (deleteModal === 'delete-confirm') deleteSelected()
      else setDeleteModal(null)
    }} />
  </div>
}

function ReadRow({ id, label, value, wide = false }: { id: string; label: string; value: string; wide?: boolean }) {
  return <div className="g_row"><div className="g_input_wrap"><span className="g_label title l_title">{label}</span><div className="l_date_range_wrap"><input id={id} className={`g_input${wide ? ' width_2x' : ''}`} readOnly value={value} /></div></div></div>
}

function DeleteDialog({ state, onNo, onYes }: { state: DeleteModal; onNo: () => void; onYes: () => void }) {
  if (!state) return null
  const success = state === 'success'
  return <div id="g_question_modal" className="g_question_modal s05-modal" role="dialog" aria-modal="true"><div className="mini_modal"><p className="question">{state === 'other-warning' ? <>본인이 등록한 스케줄이 아닙니다.<br />다른 직원이 등록한 스케줄을 삭제 하시겠습니까?</> : state === 'delete-confirm' ? '등록된 스케줄을 모두 삭제 하시겠습니까?' : '스케줄이 모두 삭제 되었습니다.'}</p><div className="flex_wrap gap_10 g_modal_button_wrap"><button id="g_yes_btn" className={`g_btn modalConfirm save${success ? '' : ' s05-confirm-yes'}`} type="button" onClick={onYes}>{success ? 'Ok' : '예(Yes)'}</button>{!success && <button id="g_no_btn" className="g_btn modalConfirm close s05-confirm-no" type="button" onClick={onNo}>아니오(No)</button>}</div></div></div>
}
